import { clearSessionCache } from '../utils/sessionCache';
import { hasTableParticipationProfile } from '../utils/tableAccess';
import dayjs from 'dayjs';
import {
  getRedirectResult,
  reauthenticateWithPopup,
  sendPasswordResetEmail,
  signInWithRedirect,
  signInWithCustomToken,
  signOut,
  updateProfile,
} from 'firebase/auth';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocFromCache,
  getDocFromServer,
  getDocs,
  limit,
  orderBy,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, auth, db, googleProvider, appleProvider } from '../firebase';

const COLLECTIONS = {
  users: 'users',
  groups: 'groups',
  feedback: 'userFeedback',
  venues: 'venues',
  languages: 'languages',
  logs: 'logs',
  notifications: 'notifications',
  settings: 'settings',
};

export const LEGAL_DOCUMENTS = {
  termsUrl: 'https://www.convivialapp.it/tc.html',
  privacyUrl: 'https://www.iubenda.com/privacy-policy/93045104',
  cookieUrl: 'https://www.iubenda.com/privacy-policy/93045104/cookie-policy',
  termsVersion: '2026-09-08',
};

const DEFAULT_TUTORIAL_STEPS = [
  {
    icon: 'add',
    title: 'Crea il tuo tavolo',
    description: 'Scegli tema, luogo e orario per proporre un incontro conviviale.',
  },
  {
    icon: 'search',
    title: 'Trova tavoli vicini',
    description: 'Esplora la mappa o la lista per scoprire occasioni interessanti.',
  },
  {
    icon: 'users',
    title: 'Partecipa',
    description: 'Unisciti ai tavoli disponibili e incontra nuove persone.',
  },
  {
    icon: 'notification',
    title: 'Resta aggiornato',
    description: 'Ricevi conferme e aggiornamenti sui tavoli a cui partecipi.',
  },
];

export const DEFAULT_TABLE_RULE_OPTIONS = [
  {
    id: 'discretion',
    icon: 'no_photography',
    title: 'Niente foto',
    text: 'Nessuna foto dei partecipanti senza consenso esplicito.',
  },
  {
    id: 'split_bill',
    icon: 'payments',
    title: 'Conto alla romana',
    text: 'Il conto viene diviso in modo equilibrato tra i partecipanti.',
  },
  {
    id: 'verified_only',
    icon: 'verified_user',
    title: 'Profili verificati',
    text: 'Il tavolo accetta solo profili completati e verificati.',
  },
  {
    id: 'punctuality',
    icon: 'schedule',
    title: 'Puntualita',
    text: "Arrivo consigliato 10 minuti prima dell'orario fissato.",
  },
  {
    id: 'professional_focus',
    icon: 'work',
    title: 'Focus professionale',
    text: 'Conversazione orientata a networking, progetti e scambio professionale.',
  },
];

export const DEFAULT_LEAVE_REASON_OPTIONS = [
  { id: 'schedule_changed', label: 'Mi e cambiato un impegno' },
  { id: 'location_too_far', label: 'Il luogo non mi e piu comodo' },
  { id: 'topic_mismatch', label: 'Il tema non fa piu per me' },
  { id: 'personal_reason', label: 'Motivo personale' },
];

export const DEFAULT_CONTACT_PREFERENCE_OPTIONS = [
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'sms', label: 'SMS' },
  { id: 'phone_call', label: 'Chiamata' },
];

function cleanUndefined(value) {
  if (Array.isArray(value)) return value.map(cleanUndefined);
  if (value && typeof value === 'object') {
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? undefined : value;
    }
    if (dayjs.isDayjs(value)) {
      return value.isValid() ? value.toDate() : undefined;
    }
    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) {
      return value;
    }
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, entry]) => entry !== undefined)
        .map(([key, entry]) => [key, cleanUndefined(entry)]),
    );
  }
  return value;
}

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  if (dayjs.isDayjs(value)) return value.toDate();
  return new Date(value);
}

function toMillis(value) {
  const date = toDate(value);
  return date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
}

function isDiscoverableGroup(group, now = Date.now()) {
  return group.type !== 'TABLE' || toMillis(group.endAt) >= now;
}

function toFirestoreDate(value) {
  if (!value) return null;
  const date = dayjs.isDayjs(value)
    ? value.toDate()
    : value instanceof Date
      ? value
      : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function normalizeInterests(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string') {
    return value.split(',').map((item) => item.trim()).filter(Boolean);
  }
  return ['Convivial'];
}

function groupUrl(groupId) {
  return `https://convivial-app.firebaseapp.com/table/${encodeURIComponent(groupId)}`;
}

function locationDistanceKm(a, b) {
  const lat1 = Number(a?.lat);
  const lng1 = Number(a?.lng);
  const lat2 = Number(b?.lat);
  const lng2 = Number(b?.lng);
  if ([lat1, lng1, lat2, lng2].some(Number.isNaN)) return Number.POSITIVE_INFINITY;
  const radius = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const s1 = Math.sin(dLat / 2) ** 2;
  const s2 = Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * radius * Math.asin(Math.sqrt(s1 + s2));
}

function mapUserDoc(snap) {
  if (!snap.exists()) return null;
  return mapUserData(snap.id, snap.data());
}

function mapUserData(id, data) {
  return {
    id,
    uid: data.uid || id,
    email: data.email || '',
    displayName: data.displayName || '',
    phoneNumber: data.phoneNumber || '',
    photoURL: data.photoURL || '',
    notification: data.notification || {},
    profile: data.profile || {},
    tutorialCompletedAt: toDate(data.tutorialCompletedAt),
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
    lastLogin: toDate(data.lastLogin),
    deletedAt: toDate(data.deletedAt),
  };
}

function hasCompleteProfile(user) {
  return Boolean(user?.profile?.firstName && user?.profile?.lastName);
}

function membershipsFromGroupData(groupId, data = {}) {
  const entries = Object.entries(data.memberships || {}).map(([userId, membership]) => ({
    id: `${groupId}_${userId}`,
    userId,
    groupId,
    role: membership?.role || (userId === data.createdBy ? 'CREATOR' : 'ATTENDEE'),
    joiningReason: membership?.joiningReason || null,
    status: membership?.status || 'JOINED',
    metadata: membership?.metadata || {},
    joinedAt: toDate(membership?.joinedAt),
    updatedAt: toDate(membership?.updatedAt),
  }));

  if (data.createdBy && !entries.some((membership) => membership.userId === data.createdBy)) {
    entries.push({
      id: `${groupId}_${data.createdBy}`,
      userId: data.createdBy,
      groupId,
      role: 'CREATOR',
      joiningReason: null,
      status: 'JOINED',
      metadata: {},
      joinedAt: toDate(data.createdAt),
      updatedAt: toDate(data.updatedAt),
    });
  }

  return entries;
}

function joinedMembershipsFromGroupDoc(snap) {
  if (!snap.exists()) return [];
  return membershipsFromGroupData(snap.id, snap.data()).filter((membership) => membership.status === 'JOINED');
}

function membershipFromGroupDoc(snap, userId) {
  if (!snap.exists() || !userId) return null;
  return membershipsFromGroupData(snap.id, snap.data()).find((membership) => membership.userId === userId) || null;
}

function mapGroupDoc(snap, attendeeCount = null, currentUserMembership = null) {
  if (!snap.exists()) return null;
  const data = snap.data();
  const joinedMemberships = membershipsFromGroupData(snap.id, data).filter((membership) => membership.status === 'JOINED');
  return {
    id: snap.id,
    name: data.name || '',
    // The document ID is authoritative. Older saved URLs can point to another
    // host or an ID that no longer matches this document.
    url: groupUrl(snap.id),
    status: data.status || 'ACTIVE',
    interests: normalizeInterests(data.interests),
    type: data.type || 'TABLE',
    venueId: data.venueId || null,
    restaurantId: data.restaurantId || null,
    booked: data.booked || null,
    description: data.description || '',
    location: data.location || {},
    startAt: toDate(data.startAt),
    endAt: toDate(data.endAt),
    limit: Number(data.limit || 5),
    note: data.note || '',
    imageUrl: data.imageUrl || '',
    metadata: data.metadata || {},
    createdBy: data.createdBy || null,
    attendeeIds: Array.isArray(data.attendeeIds) ? data.attendeeIds : joinedMemberships.map((membership) => membership.userId),
    memberships: data.memberships || {},
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
    attendeeCount: attendeeCount ?? Number(data.attendeeCount || joinedMemberships.length || 0),
    currentUserMembership,
  };
}

function mapVenueDoc(snap) {
  if (!snap.exists()) return null;
  const data = snap.data();
  return {
    id: snap.id,
    name: data.name || '',
    email: data.email || '',
    url: data.url || '',
    type: data.type || 'RESTAURANT',
    metadata: data.metadata || {},
    imageUrl: data.imageUrl || '',
    description: data.description || '',
    address: data.address || '',
    city: data.city || '',
    latitude: data.latitude || null,
    longitude: data.longitude || null,
    note: data.note || '',
    createdAt: toDate(data.createdAt),
  };
}

function participantFromProfile(user, membership) {
  const userId = user?.uid || membership.userId;
  const displayName = user?.displayName || (membership.role === 'CREATOR' ? 'Organizzatore' : 'Commensale');
  const photoURL = user?.photoURL || '';
  const role = membership.role || 'ATTENDEE';
  return {
    userId,
    displayName,
    photoURL,
    role,
    joinedAt: toDate(membership.joinedAt),
    status: membership.status || 'JOINED',
    user_id: userId,
    user_complete_name: displayName,
    user_avatar_url: photoURL,
    user_role: role,
  };
}

async function getUser(uid) {
  if (!uid) return null;
  if (auth.currentUser?.uid !== uid) {
    const readProfile = httpsCallable(getFunctions(app, 'europe-west1'), 'getPublicUserProfile');
    const { data } = await readProfile({ userId: uid });
    return data.result ? mapUserData(uid, data.result) : null;
  }
  return mapUserDoc(await getDoc(doc(db, COLLECTIONS.users, uid)));
}

async function getMembership(groupId, userId) {
  if (!groupId || !userId) return null;
  const groupSnap = await getDoc(doc(db, COLLECTIONS.groups, groupId));
  return membershipFromGroupDoc(groupSnap, userId);
}

async function getMembershipsByUser(userId) {
  if (!userId) return {};
  const groupsSnap = await getDocs(query(
    collection(db, COLLECTIONS.groups),
    where('attendeeIds', 'array-contains', userId),
  ));
  return Object.fromEntries(groupsSnap.docs.map((groupDoc) => {
    const membership = membershipFromGroupDoc(groupDoc, userId);
    return [groupDoc.id, membership];
  }));
}

const pendingUserProfiles = new Map();

export function ensureUserProfile(user, overrides = {}) {
  if (!user) return Promise.resolve(null);
  // AuthContext and the WebView login page can request the same profile together.
  // Share their work; explicit profile edits still get their own transaction.
  if (Object.keys(overrides).length > 0) return syncUserProfile(user, overrides);
  if (pendingUserProfiles.has(user.uid)) return pendingUserProfiles.get(user.uid);
  const pending = syncUserProfile(user, overrides).finally(() => {
    pendingUserProfiles.delete(user.uid);
  });
  pendingUserProfiles.set(user.uid, pending);
  return pending;
}

async function syncUserProfile(user, overrides) {
  if (!user) return null;
  const userRef = doc(db, COLLECTIONS.users, user.uid);
  try {
    // Retry from a fresh server snapshot if another client changes the profile.
    return await runTransaction(db, async (transaction) => {
      const userSnap = await transaction.get(userRef);
      const existing = userSnap.exists() ? userSnap.data() : {};
      if (existing.deletedAt) {
        const error = new Error('Profilo eliminato. Accedi nuovamente per registrarti.');
        error.code = 'auth/user-disabled';
        throw error;
      }
      const now = new Date();
      const legacyTutorialCompleted = localStorage.getItem('user_id') === user.uid
        && localStorage.getItem('skipTutorial') === 'skip';
      // Older accounts had no shared tutorial field. A previous login must not
      // force them through onboarding again on a new device. New accounts save null.
      const returningLegacyAccount = userSnap.exists() && existing.lastLogin
        && !Object.prototype.hasOwnProperty.call(existing, 'tutorialCompletedAt');
      const tutorialCompletedAt = existing.tutorialCompletedAt
        || (legacyTutorialCompleted || returningLegacyAccount ? now : null);
      const displayName = overrides.displayName ?? user.displayName ?? existing.displayName ?? user.phoneNumber ?? '';
      const payload = cleanUndefined({
        uid: user.uid,
        email: overrides.email ?? user.email ?? existing.email ?? '',
        displayName,
        phoneNumber: overrides.phoneNumber ?? existing.phoneNumber ?? '',
        photoURL: overrides.photoURL ?? existing.photoURL ?? '',
        notification: existing.notification || {},
        profile: {
          bio: '',
          interests: [],
          firstName: '',
          lastName: '',
          profession: 'Non definita',
          sector: 'Non definita',
          favouriteLocation: '',
          preferenceZones: [],
          contactPreference: 'whatsapp',
          gender: 'Non definito',
          dateBirth: null,
          ...(existing.profile || {}),
          ...(overrides.profile || {}),
        },
        tutorialCompletedAt,
        // Existing profiles cannot change createdAt under the Firestore rules.
        ...(userSnap.exists() ? {} : { createdAt: now }),
        updatedAt: now,
        lastLogin: now,
      });
      transaction.set(userRef, payload, { merge: true });
      // Return the data that was committed, without a separate potentially stale read.
      return mapUserData(user.uid, { ...existing, ...payload });
    });
  } catch (error) {
    if (error.code === 'auth/user-disabled' || error.code === 'permission-denied') {
      await signOut(auth);
      localStorage.clear();
      throw error;
    }
    console.error('Unable to sync Firebase user profile:', {
      operation: 'profile-transaction',
      code: error.code,
      userId: user.uid,
    });
    // Best effort: make future failures diagnosable without collecting auth tokens.
    if (auth.currentUser?.uid === user.uid) {
      addDoc(collection(db, COLLECTIONS.logs), {
        userId: user.uid,
        action: 'PROFILE_SYNC_FAILED',
        details: { operation: 'profile-transaction', code: error.code || 'unknown' },
        createdAt: serverTimestamp(),
      }).catch(() => {});
    }
    throw error;
  }
}

export async function getLegalConsents(userId) {
  if (!userId) return null;
  const userSnap = await getDoc(doc(db, COLLECTIONS.users, userId));
  if (!userSnap.exists()) return null;
  return userSnap.data().legal || null;
}

export async function acceptLegalDocuments(userId, newsletterMarketingConsent) {
  if (!userId) throw new Error('Missing user id');
  const userRef = doc(db, COLLECTIONS.users, userId);
  const userSnap = await getDoc(userRef);
  if (!userSnap.exists()) throw new Error('User profile not found');

  const existingLegal = userSnap.data().legal || {};
  await updateDoc(userRef, {
    legal: {
      ...existingLegal,
      termsAcceptedAt: serverTimestamp(),
      termsVersion: LEGAL_DOCUMENTS.termsVersion,
      privacyPolicyAcknowledgedAt: serverTimestamp(),
      newsletterMarketingConsent: Boolean(newsletterMarketingConsent),
      newsletterMarketingUpdatedAt: serverTimestamp(),
    },
    updatedAt: serverTimestamp(),
  });
}

export async function updateNewsletterMarketingConsent(userId, newsletterMarketingConsent) {
  if (!userId) throw new Error('Missing user id');
  const userRef = doc(db, COLLECTIONS.users, userId);
  const userSnap = await getDoc(userRef);
  if (!userSnap.exists()) throw new Error('User profile not found');

  await updateDoc(userRef, {
    legal: {
      ...(userSnap.data().legal || {}),
      newsletterMarketingConsent: Boolean(newsletterMarketingConsent),
      newsletterMarketingUpdatedAt: serverTimestamp(),
    },
    updatedAt: serverTimestamp(),
  });
}

export async function signInWithGoogle() {
  await signInWithRedirect(auth, googleProvider); 
}

export async function signInWithApple() {
  await signInWithRedirect(auth, appleProvider);
}

export async function handleRedirectSignInResult() {
  const result = await getRedirectResult(auth);

  if (!result) {
    return null;
  }
  
  const profile = await ensureUserProfile(result.user);
  syncLocalUser(profile);
  return { user: result.user, profile };
}

export async function signInWithFlutterWebLoginToken(token) {
  if (!token) {
    throw new Error('Missing Flutter web login token');
  }

  const result = await signInWithCustomToken(auth, token);
  const profile = await ensureUserProfile(result.user);

  syncLocalUser(profile);

  return {
    user: result.user,
    profile,
  };
}

export async function logout() {
  clearSessionCache();
  await signOut(auth);
  localStorage.clear();
}

export async function sendUserPasswordReset(email) {
  if (!email) throw new Error('Email mancante');
  await sendPasswordResetEmail(auth, email);
}

export async function deleteCurrentUserAccount() {
  clearSessionCache();
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('Utente non autenticato');
  const provider = currentUser.providerData.some((entry) => entry.providerId === 'apple.com')
    ? appleProvider : googleProvider;
  await reauthenticateWithPopup(currentUser, provider);
  await currentUser.getIdToken(true);
  const removeAccount = httpsCallable(getFunctions(app, 'europe-west1'), 'deleteMyAccount', { timeout: 540000 });
  await removeAccount();
  await signOut(auth);
  localStorage.clear();
}

export function syncLocalUser(profileOrUser) {
  if (!profileOrUser) return;
  const uid = profileOrUser.uid || profileOrUser.id;
  const displayName = profileOrUser.displayName || profileOrUser.user_complete_name || '';
  const photoURL = profileOrUser.photoURL || profileOrUser.user_avatar_url || '';
  const previousUid = localStorage.getItem('user_id');
  if (previousUid !== uid || profileOrUser.profile) localStorage.removeItem('skipTutorial');
  localStorage.setItem('user_id', uid);
  localStorage.setItem('user_complete_name', displayName);
  localStorage.setItem('user_avatar_url', photoURL);
  if (hasCompleteProfile(profileOrUser)) {
    localStorage.setItem('profile_completed', 'confirmed');
  }
}

export async function getUserProfile(userId, viewerUserId = null) {
  const user = await getUser(userId);
  if (!user) return null;
  const isOwnProfile = viewerUserId === userId;
  const groupStatsPromise = isOwnProfile
    ? Promise.allSettled([
      getDocs(query(collection(db, COLLECTIONS.groups), where('attendeeIds', 'array-contains', userId))),
      getDocs(query(collection(db, COLLECTIONS.groups), where('createdBy', '==', userId))),
    ])
    : getDocs(query(
      collection(db, COLLECTIONS.groups),
      where('status', '==', 'ACTIVE'),
      orderBy('startAt', 'asc'),
      limit(200),
    ));
  const [groupStatsResult, feedbackResult] = await Promise.allSettled([
    groupStatsPromise,
    getDocs(query(collection(db, COLLECTIONS.feedback), where('targetUserId', '==', userId))),
  ]);
  let attendeeCount = 0;
  let creatorCount = 0;
  if (isOwnProfile) {
    const [joinedGroupsResult, createdGroupsResult] = groupStatsResult.status === 'fulfilled' ? groupStatsResult.value : [];
    attendeeCount = joinedGroupsResult?.status === 'fulfilled' ? joinedGroupsResult.value.size : 0;
    creatorCount = createdGroupsResult?.status === 'fulfilled' ? createdGroupsResult.value.size : 0;
  } else if (groupStatsResult.status === 'fulfilled') {
    const publicGroups = groupStatsResult.value.docs.map((groupDoc) => mapGroupDoc(groupDoc)).filter(Boolean);
    attendeeCount = publicGroups.filter((group) => (group.attendeeIds || []).includes(userId)).length;
    creatorCount = publicGroups.filter((group) => group.createdBy === userId).length;
  }
  const feedbackSnap = feedbackResult.status === 'fulfilled' ? feedbackResult.value : null;
  const feedbackValues = (feedbackSnap?.docs || []).map((feedbackDoc) => Number(feedbackDoc.data().value || 0)).filter(Boolean);
  const averageFeedback = feedbackValues.length
    ? feedbackValues.reduce((total, value) => total + value, 0) / feedbackValues.length
    : 0;
  return {
    ...user,
    stats: {
      attendeeCount,
      creatorCount,
      averageFeedback,
      feedbackCount: feedbackValues.length,
    },
  };
}

export async function updateUserProfile(userId, profileInput) {
  clearSessionCache();
  const userRef = doc(db, COLLECTIONS.users, userId);
  const existingUserSnap = await getDoc(userRef);
  const existingProfile = existingUserSnap.exists() ? existingUserSnap.data().profile || {} : {};
  const preferenceZones = Array.isArray(profileInput.preferenceZones)
    ? profileInput.preferenceZones.filter(Boolean)
    : String(profileInput.preferenceZones || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  const displayName = profileInput.displayName || [profileInput.firstName, profileInput.lastName].filter(Boolean).join(' ').trim();
  const profile = cleanUndefined({
    ...existingProfile,
    firstName: profileInput.firstName,
    lastName: profileInput.lastName,
    bio: profileInput.bio,
    interests: normalizeInterests(profileInput.interests),
    profession: profileInput.profession,
    sector: profileInput.sector,
    favouriteLocation: profileInput.favouriteLocation ?? preferenceZones.join(', '),
    preferenceZones,
    contactPreference: profileInput.contactPreference,
  });
  const payload = cleanUndefined({
    uid: userId,
    displayName,
    email: profileInput.email,
    phoneNumber: profileInput.phoneNumber,
    profile,
    updatedAt: serverTimestamp(),
  });
  await updateDoc(userRef, payload);
  if (auth.currentUser?.uid === userId && displayName) {
    await updateProfile(auth.currentUser, { displayName });
  }
  clearSessionCache();
  const updated = await getUserProfile(userId);
  syncLocalUser(updated);
  return updated;
}

async function uploadImage(file, kind, groupId) {
  const base64 = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = () => reject(new Error('Impossibile leggere l’immagine.'));
    reader.readAsDataURL(file);
  });
  const upload = httpsCallable(getFunctions(app, 'europe-west1'), 'uploadProfileImage');
  const { data } = await upload({ base64, contentType: file.type || 'image/jpeg', kind, ...(groupId ? { groupId } : {}) });
  return data.url;
}

export async function uploadUserAvatar(userId, file) {
  clearSessionCache();
  if (auth.currentUser?.uid !== userId) throw new Error('Accedi per modificare il profilo.');
  const photoURL = await uploadImage(file, 'avatar');
  await updateDoc(doc(db, COLLECTIONS.users, userId), {
    photoURL,
    updatedAt: serverTimestamp(),
  });
  if (auth.currentUser?.uid === userId) {
    await updateProfile(auth.currentUser, { photoURL });
  }
  localStorage.setItem('user_avatar_url', photoURL);
  clearSessionCache();
  return photoURL;
}

export async function updateNotificationToken(userId, tokenOptions) {
  if (!userId || !tokenOptions) return;
  const now = serverTimestamp();
  const notification = {
    updatedAt: now,
  };

  const {
    firebaseToken,
    firebaseWebToken,
    firebaseNativeToken,
    permission,
    appOpenUrl,
    provider,
  } = tokenOptions;
  if (firebaseToken) notification.firebaseToken = firebaseToken;
  if (firebaseWebToken) notification.firebaseWebToken = firebaseWebToken;
  if (firebaseNativeToken) notification.firebaseNativeToken = firebaseNativeToken;
  if (permission) notification.permission = permission;
  if (appOpenUrl) notification.appOpenUrl = appOpenUrl;
  notification.provider = provider || 'firebase';
  notification.userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';

  await setDoc(doc(db, COLLECTIONS.users, userId), {
    notification,
    updatedAt: now,
  }, { merge: true });
}

export async function getGroupsNear({ latitude, longitude, radiusKm = 50, userId = null }) {
  if (userId && latitude != null && longitude != null) {
    addDoc(collection(db, COLLECTIONS.logs), {
      userId,
      action: 'getGroups',
      latitude: Number(latitude),
      longitude: Number(longitude),
      details: {
        latitude: Number(latitude),
        longitude: Number(longitude),
        radiusKm: Number(radiusKm || 50),
      },
      createdAt: serverTimestamp(),
    }).catch((error) => {
      console.error('Unable to log current map position for nearby table notifications:', error);
    });
  }
  const groupsQuery = query(
    collection(db, COLLECTIONS.groups),
    where('status', '==', 'ACTIVE'),
    orderBy('startAt', 'asc'),
    limit(200),
  );
  const groupsSnap = await getDocs(groupsQuery);
  let membershipsByGroup = {};
  if (userId) {
    try {
      membershipsByGroup = await getMembershipsByUser(userId);
    } catch (error) {
      console.error('Unable to load current user table memberships:', error);
    }
  }
  const groups = groupsSnap.docs.map((groupDoc) => {
    const data = groupDoc.data();
    const currentUserMembership = membershipsByGroup[groupDoc.id] || null;
    return mapGroupDoc(groupDoc, Number(data.attendeeCount || 0), currentUserMembership);
  });
  return groups
    .filter((group) => isDiscoverableGroup(group))
    .filter((group) => locationDistanceKm({ lat: latitude, lng: longitude }, group.location) <= Number(radiusKm || 50))
    .sort((a, b) => toMillis(a.startAt) - toMillis(b.startAt));
}

export async function getGroupDetails(groupId, userId = null) {
  const groupRef = doc(db, COLLECTIONS.groups, groupId);
  let groupSnap;
  try {
    // A shared link must resolve against the server first: a WebView can keep
    // an old Firestore cache even after the hosted app has been updated.
    groupSnap = await getDocFromServer(groupRef);
  } catch (serverError) {
    if (!['unavailable', 'deadline-exceeded'].includes(serverError?.code)) {
      throw serverError;
    }
    // Offline visitors may still see a table already saved on this device.
    // A missing cache entry is not proof that the link is invalid.
    try {
      const cachedSnap = await getDocFromCache(groupRef);
      if (cachedSnap.exists()) groupSnap = cachedSnap;
    } catch {
      // A device without a cached copy should report the original server error.
    }
    if (!groupSnap) {
      throw serverError;
    }
  }
  const group = mapGroupDoc(groupSnap);
  if (!group) {
    return {
      group: null,
      attendees: [],
    };
  }

  let currentUserMembership = null;
  if (userId) currentUserMembership = membershipFromGroupDoc(groupSnap, userId);

  let memberships = [];
  try {
    memberships = joinedMembershipsFromGroupDoc(groupSnap);
  } catch (error) {
    console.error('Unable to read table attendees:', error);
  }
  if (memberships.length === 0 && group.createdBy) {
    memberships = [{
      userId: group.createdBy,
      groupId,
      role: 'CREATOR',
      status: 'JOINED',
      joinedAt: group.createdAt,
    }];
  }
  // Every signed-in Convivial user can see diners, even before joining the table.
  const canSeeDiners = Boolean(userId && auth.currentUser?.uid === userId && !auth.currentUser?.isAnonymous);
  const visibleMemberships = canSeeDiners ? memberships : memberships.filter((membership) => membership.userId === group.createdBy);
  // The same deployed endpoint filters visitors to the public host and grants
  // keeps phone numbers private for every viewer.
  const readProfiles = httpsCallable(getFunctions(app, 'europe-west1'), 'getTableProfiles');
  const { data } = await readProfiles({ groupId });
  const usersById = data.result || {};
  const attendees = visibleMemberships
    .map((membership) => participantFromProfile(usersById[membership.userId], membership))
    .sort((a, b) => (a.role === 'CREATOR' ? -1 : 1) - (b.role === 'CREATOR' ? -1 : 1));
  currentUserMembership = memberships.find((membership) => membership.userId === userId) || currentUserMembership;
  return {
    group: mapGroupDoc(groupSnap, memberships.length, currentUserMembership),
    attendees,
  };
}

export async function resolveGroupLink(groupId) {
  const resolve = httpsCallable(getFunctions(app, 'europe-west1'), 'resolveGroupLink');
  const result = await resolve({ groupId });
  return result.data?.groupId || null;
}

// Only approved restaurants are public. The Places ID identifies the same venue on both sites.
export async function getRestaurants() {
  const snapshot = await getDocs(query(collection(db, 'restaurants'), where('status', '==', 'APPROVED')));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function getRestaurant(id) {
  const restaurants = await getRestaurants();
  return restaurants.find((restaurant) => restaurant.id === id) || null;
}

export async function getRestaurantGroups(restaurantId) {
  const snapshot = await getDocs(query(collection(db, COLLECTIONS.groups),
    where('status', '==', 'ACTIVE'), where('restaurantId', '==', restaurantId)));
  return snapshot.docs.map((item) => mapGroupDoc(item))
    .filter((group) => new Date(group.endAt).getTime() > Date.now())
    .sort((a, b) => new Date(a.startAt) - new Date(b.startAt));
}

async function restaurantForGroup(input) {
  const id = input.restaurantId || input.location?.restaurantId;
  // A Google Places ID alone does not associate a table with an approved restaurant.
  // Ordinary tables must not depend on access to the restaurant directory.
  if (!id) return null;
  const restaurant = await getRestaurant(id);
  if (!restaurant) throw new Error('Questo ristorante non è più disponibile.');
  return restaurant;
}

function restaurantLocation(restaurant) {
  return { ...restaurant.location, placeId: restaurant.id, restaurantId: restaurant.id,
    place: restaurant.name, directions: restaurant.address };
}

function groupSchedule(input, existing = {}) {
  const startAt = toFirestoreDate(input.startAt);
  if (!startAt) throw new Error('Seleziona una data di inizio valida.');

  const previousStart = toMillis(existing.startAt);
  const previousEnd = toMillis(existing.endAt);
  let endAt = input.endAt ? toFirestoreDate(input.endAt) : null;
  // The editor sends the old end when only the start changes. Move both dates
  // together, preserving a valid duration or using the default two hours.
  if (previousStart && startAt.getTime() !== previousStart
    && (!input.endAt || endAt?.getTime() === previousEnd)) {
    const duration = previousEnd > previousStart ? previousEnd - previousStart : 2 * 60 * 60 * 1000;
    endAt = new Date(startAt.getTime() + duration);
  } else if (!input.endAt) {
    endAt = dayjs(startAt).add(2, 'hour').toDate();
  }
  if (!endAt || endAt <= startAt) {
    throw new Error('La fine del tavolo deve essere successiva all’inizio.');
  }
  return { startAt, endAt };
}

export async function createGroup(userId, input) {
  clearSessionCache();
  const { startAt, endAt } = groupSchedule(input);
  const restaurant = await restaurantForGroup(input);
  const groupRef = doc(collection(db, COLLECTIONS.groups));
  let imageUrl = input.imageUrl || '';
  if (input.coverFile) {
    imageUrl = await uploadImage(input.coverFile, 'cover', groupRef.id);
  }
  const groupPayload = cleanUndefined({
    name: input.name,
    url: groupUrl(groupRef.id),
    status: 'ACTIVE',
    interests: normalizeInterests(input.interests),
    type: input.type || 'TABLE',
    venueId: restaurant?.id || input.venueId || null,
    // Omit the new field on ordinary tables so existing deployed rules still accept them.
    restaurantId: restaurant?.id || undefined,
    booked: input.booked || null,
    description: input.description || '',
    location: restaurant ? restaurantLocation(restaurant) : input.location || {},
    startAt,
    endAt,
    limit: Number(input.limit || 5),
    attendeeCount: 1,
    note: input.note || '',
    imageUrl,
    metadata: input.metadata || {},
    createdBy: userId,
    attendeeIds: [userId],
    memberships: {
      [userId]: {
        joinedAt: serverTimestamp(),
        role: 'CREATOR',
        joiningReason: null,
        status: 'JOINED',
        metadata: {},
      },
    },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  await setDoc(groupRef, groupPayload);
  clearSessionCache();
  return {
    id: groupRef.id,
    url: groupPayload.url,
  };
}

export async function updateGroup(groupId, userId, input) {
  clearSessionCache();
  const existing = await getDoc(doc(db, COLLECTIONS.groups, groupId));
  const { startAt, endAt } = groupSchedule(input, existing.data());
  const restaurantId = existing.data()?.restaurantId;
  // Keep the association and address fixed when editing an existing restaurant table.
  if (restaurantId) input = { ...input, location: existing.data().location, venueId: existing.data().venueId };
  const membership = await getMembership(groupId, userId);
  if (membership?.role !== 'CREATOR') throw new Error('Solo il creatore puo aggiornare il tavolo');
  let imageUrl = input.imageUrl;
  if (input.coverFile) {
    imageUrl = await uploadImage(input.coverFile, 'cover', groupId);
  }
  await updateDoc(doc(db, COLLECTIONS.groups, groupId), cleanUndefined({
    name: input.name,
    venueId: input.venueId || null,
    interests: normalizeInterests(input.interests),
    description: input.description || '',
    location: input.location || {},
    startAt,
    endAt,
    limit: Number(input.limit || 5),
    imageUrl,
    metadata: input.metadata || {},
    updatedAt: serverTimestamp(),
  }));
  clearSessionCache();
  return { id: groupId };
}

export async function cancelGroup(groupId, userId) {
  clearSessionCache();
  const membership = await getMembership(groupId, userId);
  if (membership?.role !== 'CREATOR') throw new Error('Solo il creatore puo cancellare il tavolo');
  await updateDoc(doc(db, COLLECTIONS.groups, groupId), {
    status: 'CANCELLED',
    updatedAt: serverTimestamp(),
  });
  clearSessionCache();
}

export async function joinGroup(groupId, userId) {
  clearSessionCache();
  if (!auth.currentUser || auth.currentUser.isAnonymous || auth.currentUser.uid !== userId) {
    throw new Error('Accedi per partecipare al tavolo.');
  }
  const savedProfile = await getUser(userId);
  if (!hasTableParticipationProfile(savedProfile)) {
    throw new Error('Per partecipare completa il profilo con nome, cognome e cellulare.');
  }
  const { group, attendees } = await getGroupDetails(groupId, userId);
  if (!group || group.status !== 'ACTIVE') throw new Error('Tavolo non disponibile');
  if (toMillis(group.startAt) <= Date.now()) throw new Error('Non puoi partecipare da Convivial a un tavolo gia iniziato');
  if (group.currentUserMembership?.status === 'JOINED' || attendees.some((attendee) => attendee.userId === userId)) return;
  if (Number(group.attendeeCount || attendees.length) >= group.limit) throw new Error('Tavolo completo');
  await updateDoc(doc(db, COLLECTIONS.groups, groupId), {
    attendeeIds: [...new Set([...(group.attendeeIds || attendees.map((attendee) => attendee.userId)), userId])],
    attendeeCount: Number(group.attendeeCount || attendees.length || 0) + 1,
    [`memberships.${userId}`]: {
      joinedAt: serverTimestamp(),
      role: 'ATTENDEE',
      joiningReason: null,
      status: 'JOINED',
      metadata: {},
    },
    updatedAt: serverTimestamp(),
  });
  clearSessionCache();
}

export async function leaveGroup(groupId, userId, leaveReasonIds = []) {
  clearSessionCache();
  const membership = await getMembership(groupId, userId);
  if (!membership || membership.status !== 'JOINED') return;
  const { group, attendees } = await getGroupDetails(groupId, userId);
  await updateDoc(doc(db, COLLECTIONS.groups, groupId), {
    attendeeIds: (group.attendeeIds || []).filter((attendeeId) => attendeeId !== userId),
    attendeeCount: Math.max(Number(group.attendeeCount || attendees.length || 1) - 1, 0),
    [`memberships.${userId}`]: {
      joinedAt: membership.joinedAt,
      role: membership.role || 'ATTENDEE',
      joiningReason: membership.joiningReason || null,
      status: 'CANCELLED',
      metadata: {
        ...(membership.metadata || {}),
        leaveReasonIds: Array.isArray(leaveReasonIds) ? leaveReasonIds : [],
      },
      updatedAt: serverTimestamp(),
    },
    updatedAt: serverTimestamp(),
  });
  clearSessionCache();
}

export async function reportGroup(groupId, userId) {
  await addDoc(collection(db, COLLECTIONS.logs), {
    userId,
    action: 'GROUP_REPORTED',
    details: { groupId },
    createdAt: serverTimestamp(),
  });
}

export async function getGroupsForUser(userId, viewerUserId = null) {
  if (!userId) return [];
  const isOwnProfile = viewerUserId === userId;
  const groupsQuery = isOwnProfile
    ? query(collection(db, COLLECTIONS.groups), where('attendeeIds', 'array-contains', userId))
    : query(
      collection(db, COLLECTIONS.groups),
      where('status', '==', 'ACTIVE'),
      orderBy('startAt', 'asc'),
      limit(200),
    );
  const groupsSnap = await getDocs(groupsQuery);
  const groups = groupsSnap.docs.map((groupSnap) => {
    const membership = membershipFromGroupDoc(groupSnap, userId);
    const attendeeCount = joinedMembershipsFromGroupDoc(groupSnap).length;
    return mapGroupDoc(groupSnap, attendeeCount, membership);
  });
  return groups.filter((group) => (
    group &&
    (isOwnProfile || (group.attendeeIds || []).includes(userId))
  ));
}

// Missing or private related documents must not hide readable feedback.
async function optionalFeedbackData(load) {
  try {
    return await load();
  } catch (error) {
    console.error('Unable to load related feedback data:', error.code);
    return null;
  }
}

export async function getFeedbackFormData(groupId, userId) {
  const { group, attendees } = await getGroupDetails(groupId, userId);
  if (!group) return { state: 'not_possible' };
  const existingSnap = await getDocs(query(
    collection(db, COLLECTIONS.feedback),
    where('fromUserId', '==', userId),
  ));
  // Filter this user's feedback locally: opening the form needs no composite index.
  const existingFeedbacks = existingSnap.docs.map((item) => item.data())
    .filter((item) => item.groupId === groupId);
  const isAttendee = attendees.some((attendee) => attendee.userId === userId);
  const hasOtherDiners = new Set(attendees.map((attendee) => attendee.userId)).size >= 2;
  const ended = toMillis(group.endAt) <= Date.now();
  const creator = attendees.find((attendee) => attendee.role === 'CREATOR') || null;
  let venue = null;
  if (group.restaurantId) {
    venue = await optionalFeedbackData(() => getRestaurant(group.restaurantId));
  } else if (group.type === 'VENUE' && group.venueId) {
    venue = await optionalFeedbackData(async () => mapVenueDoc(await getDoc(doc(db, COLLECTIONS.venues, group.venueId))));
  }
  // Ordinary tables carry Google Places IDs, not documents in the venues collection.
  return {
    state: existingFeedbacks.length ? 'done' : (isAttendee && hasOtherDiners && ended ? 'to_do' : 'not_possible'),
    existingFeedbacks,
    group,
    venue,
    owner: creator?.userId !== userId ? creator : null,
    users: attendees.filter((attendee) => attendee.userId !== userId && attendee.role !== 'CREATOR'),
  };
}

export async function submitFeedback(groupId, userId, values) {
  const { state, group, venue, owner, users } = await getFeedbackFormData(groupId, userId);
  if (!group) throw new Error('Tavolo non trovato');
  if (state === 'done') throw new Error('Hai già inviato il feedback per questo tavolo.');
  if (state !== 'to_do') throw new Error('Puoi lasciare feedback solo dopo la fine di un tavolo a cui hai partecipato.');
  if (!owner && !venue && !users.length) throw new Error('Non ci sono commensali da valutare.');
  const batch = writeBatch(db);
  const addFeedback = ({ targetUserId = null, targetVenueId = null, targetType, value }) => {
    const rating = Number(value);
    if (!Number.isInteger(rating) || rating < 1 || rating > 5) {
      throw new Error('Seleziona da 1 a 5 stelle per ogni valutazione.');
    }
    // Stable IDs also stop two simultaneous submissions from creating duplicates.
    const feedbackId = [groupId, userId, targetType, targetUserId || targetVenueId]
      .map(encodeURIComponent).join('_');
    const feedbackRef = doc(db, COLLECTIONS.feedback, feedbackId);
    batch.set(feedbackRef, cleanUndefined({
      fromUserId: userId,
      targetUserId,
      targetVenueId,
      targetType,
      groupId,
      value: rating,
      note: '',
      createdAt: serverTimestamp(),
    }));
  };
  if (owner?.userId) addFeedback({ targetUserId: owner.userId, targetType: 'USER', value: values.ownerFeedback });
  if (venue?.id) addFeedback({ targetVenueId: venue.id, targetType: 'VENUE', value: values.venueFeedback });
  users.forEach((user) => {
    addFeedback({ targetUserId: user.userId, targetType: 'USER', value: values.userFeedbacks?.[user.userId] });
  });
  await batch.commit();
}

export async function listFeedbacks(userId, feedbackType) {
  const feedbackQuery = feedbackType === 'sent'
    ? query(collection(db, COLLECTIONS.feedback), where('fromUserId', '==', userId), orderBy('createdAt', 'desc'))
    : query(collection(db, COLLECTIONS.feedback), where('targetUserId', '==', userId), orderBy('createdAt', 'desc'));
  const feedbackSnap = await getDocs(feedbackQuery);
  const rows = await Promise.all(feedbackSnap.docs.map(async (feedbackDoc) => {
    const feedback = { id: feedbackDoc.id, ...feedbackDoc.data() };
    const otherUserId = feedbackType === 'sent' ? feedback.targetUserId : feedback.fromUserId;
    const [otherUser, groupSnap] = await Promise.all([
      otherUserId ? optionalFeedbackData(() => getUser(otherUserId)) : null,
      feedback.groupId ? optionalFeedbackData(() => getDoc(doc(db, COLLECTIONS.groups, feedback.groupId))) : null,
    ]);
    const group = groupSnap ? mapGroupDoc(groupSnap) : null;
    return {
      id: feedback.id,
      fromUserId: feedback.fromUserId,
      targetUserId: feedback.targetUserId,
      groupId: feedback.groupId,
      value: Number(feedback.value || 0),
      note: feedback.note || '',
      createdAt: toDate(feedback.createdAt),
      user: otherUser,
      group,
    };
  }));
  const targetUser = await optionalFeedbackData(() => getUser(userId));
  const values = rows.map((row) => row.value).filter(Boolean);
  return {
    feedbacks: rows,
    user: targetUser || { uid: userId, displayName: 'Profilo non disponibile', photoURL: '' },
    total: rows.length,
    average: values.length ? values.reduce((total, value) => total + value, 0) / values.length : 0,
  };
}

export async function listNotifications(userId, page = 1, pageSize = 20) {
  const [userSnap, allSnap] = await Promise.all([
    getDocs(query(collection(db, COLLECTIONS.notifications), where('targetUserId', '==', userId), orderBy('createdAt', 'desc'), limit(100))),
    getDocs(query(collection(db, COLLECTIONS.notifications), where('target', '==', 'ALL'), orderBy('createdAt', 'desc'), limit(100))),
  ]);
  const notifications = [...userSnap.docs, ...allSnap.docs]
    .map((notificationDoc) => ({ id: notificationDoc.id, ...notificationDoc.data() }))
    .sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
  const start = (page - 1) * pageSize;
  const pageNotifications = await Promise.all(notifications.slice(start, start + pageSize).map(async (notification) => {
    let groupId = notification.groupId || null;
    const legacyFeedback = /^feedback-received:([^:]+):/.exec(notification.dedupeKey || '');
    if (!groupId && legacyFeedback) {
      try {
        // Older feedback notifications saved the review ID rather than the table ID.
        const review = await getDoc(doc(db, COLLECTIONS.feedback, legacyFeedback[1]));
        groupId = review.exists() ? review.data().groupId || null : null;
      } catch (error) {
        console.error('Unable to resolve legacy feedback notification:', error);
      }
    }
    return {
      id: notification.id,
      content: { ...notification.notification_content, ...notification.content },
      groupId,
      type: notification.type || null,
      dedupeKey: notification.dedupeKey || null,
      target: notification.target || null,
      targetUserId: notification.targetUserId || null,
      request: notification.request || null,
      response: notification.response || null,
      createdAt: toDate(notification.createdAt),
    };
  }));
  return {
    notifications: pageNotifications,
    totalPages: Math.max(1, Math.ceil(notifications.length / pageSize)),
  };
}

export function subscribeToTutorialCompletion(userId, onChange) {
  return onSnapshot(doc(db, COLLECTIONS.users, userId), (snapshot) => {
    if (snapshot.exists()) onChange(Boolean(snapshot.data().tutorialCompletedAt));
  }, (error) => console.error('Unable to synchronize tutorial completion:', error));
}

export async function completeUserTutorial(userId) {
  if (!userId || auth.currentUser?.uid !== userId) throw new Error('Accedi per completare il tutorial.');
  // A transaction makes completion idempotent when two devices finish together.
  await runTransaction(db, async (transaction) => {
    const userRef = doc(db, COLLECTIONS.users, userId);
    const userSnap = await transaction.get(userRef);
    if (!userSnap.exists()) throw new Error('Profilo non trovato. Riprova.');
    if (userSnap.data().tutorialCompletedAt) return;
    transaction.set(userRef, { tutorialCompletedAt: serverTimestamp(), updatedAt: serverTimestamp() }, { merge: true });
  });
}

export async function getTutorialSteps() {
  try {
    const tutorialSnap = await getDoc(doc(db, COLLECTIONS.settings, 'tutorial'));
    if (!tutorialSnap.exists()) return DEFAULT_TUTORIAL_STEPS;
    const steps = tutorialSnap.data().steps;
    return Array.isArray(steps) && steps.length > 0 ? steps : DEFAULT_TUTORIAL_STEPS;
  } catch (error) {
    console.error('Unable to load tutorial settings from Firestore:', error);
    return DEFAULT_TUTORIAL_STEPS;
  }
}

export async function getTableRuleOptions() {
  try {
    const rulesSnap = await getDoc(doc(db, COLLECTIONS.settings, 'tableRules'));
    if (!rulesSnap.exists()) return DEFAULT_TABLE_RULE_OPTIONS;
    const rules = rulesSnap.data().rules;
    return Array.isArray(rules) && rules.length > 0 ? rules : DEFAULT_TABLE_RULE_OPTIONS;
  } catch (error) {
    console.error('Unable to load table rule settings from Firestore:', error);
    return DEFAULT_TABLE_RULE_OPTIONS;
  }
}

export async function getLeaveReasonOptions() {
  try {
    const reasonsSnap = await getDoc(doc(db, COLLECTIONS.settings, 'leaveReasons'));
    if (!reasonsSnap.exists()) return DEFAULT_LEAVE_REASON_OPTIONS;
    const reasons = reasonsSnap.data().items;
    return Array.isArray(reasons) && reasons.length > 0 ? reasons : DEFAULT_LEAVE_REASON_OPTIONS;
  } catch (error) {
    console.error('Unable to load leave reason settings from Firestore:', error);
    return DEFAULT_LEAVE_REASON_OPTIONS;
  }
}

export async function getContactPreferenceOptions() {
  try {
    const preferencesSnap = await getDoc(doc(db, COLLECTIONS.settings, 'contactPreferences'));
    if (!preferencesSnap.exists()) return DEFAULT_CONTACT_PREFERENCE_OPTIONS;
    const preferences = preferencesSnap.data().items;
    return Array.isArray(preferences) && preferences.length > 0 ? preferences : DEFAULT_CONTACT_PREFERENCE_OPTIONS;
  } catch (error) {
    console.error('Unable to load contact preference settings from Firestore:', error);
    return DEFAULT_CONTACT_PREFERENCE_OPTIONS;
  }
}

export async function getLanguages() {
  const languagesSnap = await getDocs(query(collection(db, COLLECTIONS.languages), orderBy('key', 'asc')));
  return languagesSnap.docs.map((languageDoc) => ({ id: languageDoc.id, ...languageDoc.data() }));
}

export async function getSettings() {
  const settingsSnap = await getDocs(collection(db, COLLECTIONS.settings));
  return Object.fromEntries(settingsSnap.docs.map((settingDoc) => [settingDoc.id, settingDoc.data()]));
}

export { COLLECTIONS };
